import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { isThisMonth } from 'date-fns/isThisMonth';
import { useCallback } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { useQueryClient } from '@eika-infrastruktur/react-query';
import { formatCurrencyValue } from '@eika/currency-input';

import { useConfig } from '../../../../defaultConfig';
import { ensureAccountsData, useAccounts } from '../../../../fetchHooks/useAccount';
import usePaymentLimits, { ensurePaymentLimitsData } from '../../../../fetchHooks/usePaymentLimits';
import type { Account, PaymentLimits } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { isValidAccountPaymentFrom } from '../../../../services/accountFilters';
import TranslationsConfigType from '../../../../translations/translationType';
import { doesAmountExceedAccountBalanceAccountBalance, isNotToday } from '../../../../utils/validation';
import { UseValidateHook } from '../../../../utils/validation/validateTypes';
import { invalidDate } from '../../validate';
import { CrossBorderPaymentState } from './state';

export interface CrossBorderError {
    accountId?: string;
    amount?: string;
    amountLimit?: string;
    paymentDate?: string;
    paymentDescription?: string;
    messageReceiver?: string;
}

export interface receiverError {
    receiverName?: string;
    bankCountryCode?: string;
    accountNumber?: string;
    nationalId?: string;
    bic?: string;
    creditorDetails?: string;
    postcode?: string;
    town?: string;
    receiverCountryCode?: string;
}

export interface AmountFieldsError {
    amount?: string;
    amountLimit?: string;
    currency?: string;
    transferCurrency?: string;
    messageReceiver?: string;
    bic?: string;
}

export const checkForNumbersInName = (name: string): boolean => {
    return /\d/.test(name);
};

export const checkForOnlyNumbersInName = (name: string): boolean => {
    const removeSpacesFromName = name.replace(/\s+/g, '');
    return /^\d+$/.test(removeSpacesFromName);
};

export const isNoCoverage = (state: CrossBorderPaymentState, targetFromAccount: Account | undefined): boolean => {
    const { fromAccount, paymentDate, firstBankingDateFromToday, amountInNok } = state;
    const isBankDaysBetweenNowAndPaymentDate =
        (firstBankingDateFromToday && paymentDate && isBefore(firstBankingDateFromToday, paymentDate)) ||
        isEqual(paymentDate!, firstBankingDateFromToday!);

    if (!fromAccount || !targetFromAccount) return false;
    if (!amountInNok) return false;
    if (isBankDaysBetweenNowAndPaymentDate && isNotToday(paymentDate)) return false;

    return doesAmountExceedAccountBalanceAccountBalance(targetFromAccount, amountInNok);
};

export const isDomesticPayment = (state: CrossBorderPaymentState): boolean => {
    const { receiver, currency } = state;
    return receiver?.bankCountryCode?.value === 'NO' && currency === 'NOK';
};
export const validateNewReceiver = (
    translated: TranslationsConfigType,
    state: CrossBorderPaymentState,
): receiverError => {
    const errors: receiverError = {
        receiverName: undefined,
        bankCountryCode: undefined,
        accountNumber: undefined,
        nationalId: undefined,
        bic: undefined,
        creditorDetails: undefined,
        receiverCountryCode: undefined,
    };
    const { receiver, country, bankValidation } = state;

    if (state.receiverName === '') {
        errors.receiverName = translated.crossBorderPayment.newReceiver.receiverNameError;
    }
    if (!/[a-zA-Z]/.test(state.receiverName ?? '')) {
        errors.receiverName = translated.crossBorderPayment.needOneCharacter;
    }
    if (!(receiver?.bankCountryCode?.value || country)) {
        errors.bankCountryCode = translated.crossBorderPayment.newReceiver.selectedCountryError;
    }
    if (!receiver?.accountNumber) {
        errors.accountNumber = translated.crossBorderPayment.newReceiver.missingAccountNumber;
    }
    if (country?.requiresCreditorDetails) {
        if (!receiver?.address) {
            errors.creditorDetails = translated.crossBorderPayment.newReceiver.missingCreditorDetails;
        }
        if (!receiver?.receiverCountryCode) {
            errors.receiverCountryCode = translated.crossBorderPayment.newReceiver.selectedCountryError;
        }
    }
    if (receiver?.accountType !== 'IBAN') {
        if (country?.requiresBic) {
            if (!bankValidation?.bank?.bic) {
                errors.bic = translated.crossBorderPayment.newReceiver.missingBankBic;
            }
            if (!bankValidation?.bank?.bic && !receiver?.bic) {
                errors.bic = translated.crossBorderPayment.newReceiver.missingBic;
            }
        }
        if (country?.nationalIdRequirements?.required) {
            if (!(receiver?.nationalId || bankValidation?.bank?.nationalId)) {
                errors.nationalId = translated.crossBorderPayment.newReceiver.missingNationalId;
            }
        }
    }

    return errors;
};

export const validateUpdateReceiver = (
    translated: TranslationsConfigType,
    state: CrossBorderPaymentState,
): receiverError => {
    const errors: receiverError = {
        receiverName: undefined,
        bankCountryCode: undefined,
        accountNumber: undefined,
        nationalId: undefined,
        bic: undefined,
        creditorDetails: undefined,
        postcode: undefined,
        town: undefined,
        receiverCountryCode: undefined,
    };
    if (state.receiver?.name === '') {
        errors.receiverName = translated.crossBorderPayment.newReceiver.receiverNameError;
    }
    if (checkForOnlyNumbersInName(state.receiver?.name ?? '')) {
        errors.receiverName = translated.crossBorderPayment.needOneCharacter;
    }
    if (state.country?.requiresCreditorDetails) {
        if (state.receiver?.address === '') {
            errors.creditorDetails = translated.crossBorderPayment.newReceiver.missingCreditorDetails;
        }
    }
    if (!state.receiver?.receiverCountryCode) {
        errors.receiverCountryCode = translated.crossBorderPayment.newReceiver.missingCreditorDetails;
    }

    return errors;
};

export const validateIBANNumber = (
    translated: TranslationsConfigType,
    ibanNumber: string,
    state: CrossBorderPaymentState,
) => {
    const { country } = state;

    if (!ibanNumber) {
        return { iban: translated.crossBorderPayment.newReceiver.missingAccountNumber };
    }
    if (ibanNumber.slice(0, 2).toLowerCase() !== country?.countryCode?.value.toLowerCase()) {
        return { iban: translated.crossBorderPayment.newReceiver.errorIbanCountryCode };
    }
    if (ibanNumber.length !== country?.ibanLength) {
        return { iban: translated.crossBorderPayment.newReceiver.errorIban };
    }
    return undefined;
};

export const validateBBANNumber = (
    translated: TranslationsConfigType,
    bbanNumber: string,
    state: CrossBorderPaymentState,
) => {
    let bbanNumberValidation: string[] | undefined;
    let bbanAlphaValidation: string[] | undefined;

    // TODO: Change to using regEx when TE have fixed bban
    if (state.country?.bban) {
        const numericValidationOfBban = state.country?.bban?.find((numericBban) => numericBban.includes('n'));
        const alphaValidationOfBban = state.country?.bban?.find((alphaBban) => alphaBban.includes('a'));
        bbanNumberValidation = numericValidationOfBban?.replace(/[[\]n]+/g, '').split('-');
        bbanAlphaValidation = alphaValidationOfBban?.replace(/[[\]a]+/g, '').split('-');
    }

    if (bbanNumber.length === 0) {
        return { bban: translated.crossBorderPayment.newReceiver.missingAccountNumber };
    }
    if (bbanNumberValidation) {
        if (
            bbanNumberValidation.length === 2 &&
            (bbanNumber.length < Number(bbanNumberValidation[0]) || bbanNumber.length > Number(bbanNumberValidation[1]))
        ) {
            return { bban: translated.crossBorderPayment.newReceiver.accountNumberError };
        }
        if (bbanNumberValidation.length === 1 && bbanNumber.length !== Number(bbanNumberValidation[0])) {
            return { bban: translated.crossBorderPayment.newReceiver.accountNumberError };
        }
    }

    if (bbanAlphaValidation) {
        if (
            bbanAlphaValidation.length === 2 &&
            (bbanNumber.length < Number(bbanAlphaValidation[0]) || bbanNumber.length > Number(bbanAlphaValidation[1]))
        ) {
            return { bban: translated.crossBorderPayment.newReceiver.accountNumberError };
        }
        if (bbanAlphaValidation.length === 1 && bbanNumber.length !== Number(bbanAlphaValidation[0])) {
            return { bban: translated.crossBorderPayment.newReceiver.accountNumberError };
        }
    }

    return undefined;
};

export const validateBIC = (translated: TranslationsConfigType, bic: string) => {
    if (bic.length === 0) {
        return { bic: translated.crossBorderPayment.newReceiver.missingBic };
    }
    if (bic.length !== 8 && bic.length !== 11) {
        return { bic: translated.crossBorderPayment.newReceiver.bicError };
    }
    return undefined;
};

export const validateNationalIdNumber = (
    translated: TranslationsConfigType,
    nationalId: string,
    state: CrossBorderPaymentState,
) => {
    if (nationalId.length !== state.country?.nationalIdRequirements?.length) {
        return { nationalId: translated.crossBorderPayment.newReceiver.nationalIdError };
    }
    return undefined;
};

export const validateAmount = (
    translated: TranslationsConfigType,
    formState: CrossBorderPaymentState,
    paymentLimits: PaymentLimits | undefined,
    targetFromAccount: Account | undefined,
) => {
    const { amount, paymentDate, amountInNok, receiver, currency, country } = formState;
    if (!amount || amount <= 0) {
        return { amount: translated.paymentValidation.chooseAmount };
    }
    if (isNoCoverage(formState, targetFromAccount)) {
        return { amount: translated.paymentValidation.noCoverage };
    }
    if (currency !== country?.currency && receiver?.needBic && !receiver?.bic) {
        return { bic: translated.crossBorderPayment.newReceiver.missingBic };
    }
    if (paymentLimits && amountInNok && amountInNok > paymentLimits.maxTransactionAmount) {
        return {
            amountLimit: format(
                translated.paymentValidation.amountExceedsMaximum,
                formatCurrencyValue(paymentLimits.maxTransactionAmount),
            ),
        };
    }
    if (
        isThisMonth(paymentDate ?? getZonedDate()) &&
        paymentLimits &&
        amountInNok &&
        amountInNok >
            paymentLimits.maxMonthlyTransactionAmount - (paymentLimits.accumulatedMonthlyTransactionAmount ?? 0)
    ) {
        return {
            amountLimit: format(
                translated.paymentValidation.amountExceedsMonthlyMaximum,
                formatCurrencyValue(paymentLimits.maxMonthlyTransactionAmount),
            ),
        };
    }
    return undefined;
};

export const validateAmountFields = (
    translated: TranslationsConfigType,
    state: CrossBorderPaymentState,
    needTransferCurrency: boolean | undefined,
    paymentLimits: PaymentLimits | undefined,
    targetFromAccount: Account | undefined,
): AmountFieldsError => {
    const errors: AmountFieldsError = {
        amount: undefined,
        amountLimit: undefined,
        currency: undefined,
        transferCurrency: undefined,
        messageReceiver: undefined,
        bic: undefined,
    };

    const { amount, paymentDate, messageReceiver, amountInNok, transferCurrency, receiver, currency, country } = state;

    if (!amount || amount <= 0) {
        errors.amount = translated.paymentValidation.chooseAmount;
    } else if (isNoCoverage(state, targetFromAccount)) {
        errors.amount = translated.paymentValidation.noCoverage;
    } else if (paymentLimits && amountInNok && amountInNok > paymentLimits.maxTransactionAmount) {
        errors.amountLimit = format(
            translated.paymentValidation.amountExceedsMaximum,
            formatCurrencyValue(paymentLimits.maxTransactionAmount),
        );
    } else if (
        isThisMonth(paymentDate ?? getZonedDate()) &&
        paymentLimits &&
        amountInNok &&
        amountInNok >
            paymentLimits.maxMonthlyTransactionAmount - (paymentLimits.accumulatedMonthlyTransactionAmount ?? 0)
    ) {
        errors.amountLimit = format(
            translated.paymentValidation.amountExceedsMonthlyMaximum,
            formatCurrencyValue(paymentLimits.maxMonthlyTransactionAmount),
        );
    }

    if (!messageReceiver) {
        errors.messageReceiver = translated.crossBorderPayment.description.messageError;
    }

    if (needTransferCurrency) {
        if (!transferCurrency) errors.transferCurrency = translated.currencyField.missingTransferCurrency;
    }

    if (currency !== country?.currency && receiver?.needBic && !receiver?.bic) {
        errors.bic = translated.crossBorderPayment.newReceiver.missingBic;
    }

    return errors;
};

const validate =
    (translated: TranslationsConfigType, paymentLimits: PaymentLimits | undefined, accounts: Account[]) =>
    (formState: CrossBorderPaymentState): CrossBorderError => {
        const errors: CrossBorderError = {
            accountId: undefined,
            amount: undefined,
            amountLimit: undefined,
            paymentDate: undefined,
            paymentDescription: undefined,
            messageReceiver: undefined,
        };

        const { fromAccount, amount, paymentDate, paymentDescription, messageReceiver, amountInNok } = formState;
        const targetFromAccount = accounts
            .filter(isValidAccountPaymentFrom)
            .find((account) => account.accountNumber.value === fromAccount?.value);
        if (!fromAccount || !targetFromAccount?.accountId || targetFromAccount?.accountId.value === '') {
            errors.accountId = translated.paymentValidation.chooseFromAccount;
        }

        if (!amount || amount <= 0) {
            errors.amount = translated.paymentValidation.chooseAmount;
        } else if (isNoCoverage(formState, targetFromAccount)) {
            errors.amount = translated.paymentValidation.noCoverage;
        } else if (paymentLimits && amountInNok && amountInNok > paymentLimits.maxTransactionAmount) {
            errors.amountLimit = format(
                translated.paymentValidation.amountExceedsMaximum,
                formatCurrencyValue(paymentLimits.maxTransactionAmount),
            );
        } else if (
            isThisMonth(paymentDate ?? getZonedDate()) &&
            paymentLimits &&
            amountInNok &&
            amountInNok >
                paymentLimits.maxMonthlyTransactionAmount - (paymentLimits.accumulatedMonthlyTransactionAmount ?? 0)
        ) {
            errors.amountLimit = format(
                translated.paymentValidation.amountExceedsMonthlyMaximum,
                formatCurrencyValue(paymentLimits.maxMonthlyTransactionAmount),
            );
        }

        if (!paymentDescription) {
            errors.paymentDescription = translated.crossBorderPayment.description.descriptionError;
        }

        if (!messageReceiver) {
            errors.messageReceiver = translated.crossBorderPayment.description.messageError;
        }

        if (invalidDate(paymentDate)) {
            errors.paymentDate = translated.paymentValidation.chooseValidDate;
        }
        return errors;
    };

const useValidate: UseValidateHook<CrossBorderPaymentState, CrossBorderError> = () => {
    const queryClient = useQueryClient();
    const translated = useTranslation();
    const { featureToggles } = useConfig();

    const paymentLimits = usePaymentLimits();
    const { data: accounts } = useAccounts();

    return useCallback(
        async (state) => {
            const currentPaymentLimits = await ensurePaymentLimitsData(queryClient, featureToggles.enablePaymentLimits);
            const currentAccounts = await ensureAccountsData(queryClient);
            return validate(translated, paymentLimits.data ?? currentPaymentLimits, accounts ?? currentAccounts)(state);
        },
        [paymentLimits, translated, queryClient, accounts, featureToggles.enablePaymentLimits],
    );
};

export default useValidate;
